/**
 * Branch-timezone arithmetic, the one place every app (api, gateway, web)
 * turns an instant into a local calendar day, hour or offset.
 *
 * Pure functions on IANA zone ids (`Branch.timezone`), built on `Intl` only so
 * daylight saving is handled by the runtime and no dependency is added. The
 * offset is always derived for the instant in question, never cached, so a
 * zone with DST gives the right answer on both sides of a change.
 */
import { EAT_TIME_ZONE } from './eat.util';

const MS_PER_MIN = 60_000;

/** Used when a branch has no usable zone. Matches the `branches.timezone` column default. */
export const DEFAULT_TIME_ZONE = EAT_TIME_ZONE;

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** IANA ids are `Area/City` (or `UTC`); the runtime also accepts abbreviations such as `EAT` or `EST`, which are ambiguous. */
const IANA_SHAPE = /^(UTC|[A-Za-z_]+(\/[A-Za-z0-9_+-]+)+)$/;

export function isValidTimeZone(timeZone: string): boolean {
  if (typeof timeZone !== 'string' || !IANA_SHAPE.test(timeZone)) return false;
  try {
    formatterFor(timeZone);
    return true;
  } catch {
    return false;
  }
}

/** Falls back to the default zone for a missing or unknown id, so one bad row cannot break a report. */
export function resolveTimeZone(timeZone: string | null | undefined): string {
  return timeZone && isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIME_ZONE;
}

interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function localParts(timeZone: string, at: Date): LocalParts {
  const out: Record<string, number> = {};
  for (const p of formatterFor(resolveTimeZone(timeZone)).formatToParts(at)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return out as unknown as LocalParts;
}

/** Minutes the zone is ahead of UTC at this instant (Nairobi: 180). */
export function utcOffsetMinutes(timeZone: string, at: Date): number {
  const p = localParts(timeZone, at);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / MS_PER_MIN);
}

/** `+03:00` style offset, for device commands and display. */
export function formatUtcOffset(timeZone: string, at: Date): string {
  const total = utcOffsetMinutes(timeZone, at);
  const sign = total < 0 ? '-' : '+';
  const abs = Math.abs(total);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

/** `YYYY-MM-DD` of the instant on the zone's calendar. */
export function localDateIso(timeZone: string, at: Date): string {
  const p = localParts(timeZone, at);
  return `${String(p.year).padStart(4, '0')}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Hour of day, 0-23, on the zone's clock. */
export function localHour(timeZone: string, at: Date): number {
  return localParts(timeZone, at).hour;
}

/** Minutes past local midnight, 0-1439. */
export function localMinutesOfDay(timeZone: string, at: Date): number {
  const p = localParts(timeZone, at);
  return p.hour * 60 + p.minute;
}

/** The UTC instant at which local calendar day `dateIso` (`YYYY-MM-DD`) begins in the zone. */
export function startOfLocalDay(timeZone: string, dateIso: string): Date {
  const [y, m, d] = dateIso.split('-').map(Number);
  const midnightAsUtc = Date.UTC(y, m - 1, d);
  // Offset can differ between the guess and the real instant across a DST change; settle in two passes.
  let guess = midnightAsUtc - utcOffsetMinutes(timeZone, new Date(midnightAsUtc)) * MS_PER_MIN;
  guess = midnightAsUtc - utcOffsetMinutes(timeZone, new Date(guess)) * MS_PER_MIN;
  return new Date(guess);
}

/** Wall-clock `HH:MM` on the zone's clock. */
export function localTimeHm(timeZone: string, at: Date): string {
  const p = localParts(timeZone, at);
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

/**
 * The UTC instant for a naive wall-clock reading in the zone, e.g. a device's
 * `2026-06-03 08:00:00`. Returns an Invalid Date for text that is not a timestamp.
 * A reading that does not exist (skipped by a DST change) resolves just after the gap.
 */
export function zonedTimeToUtc(timeZone: string, local: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local.trim());
  if (!m) return new Date(NaN);
  const [y, mo, d, h, mi, s] = [m[1], m[2], m[3], m[4], m[5], m[6] ?? '0'].map(Number);
  const asUtc = Date.UTC(y, mo - 1, d, h, mi, s);
  const check = new Date(asUtc);
  // Date.UTC rolls 2026-13-40 over into a real date; a reading that does not exist is invalid.
  if (check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d || h > 23 || mi > 59 || s > 59) return new Date(NaN);
  let guess = asUtc - utcOffsetMinutes(timeZone, new Date(asUtc)) * MS_PER_MIN;
  guess = asUtc - utcOffsetMinutes(timeZone, new Date(guess)) * MS_PER_MIN;
  return new Date(guess);
}
